"use client"

import React from "react";
import {Controller, useForm} from "react-hook-form";
import {valibotResolver} from "@hookform/resolvers/valibot";
import {
    MOVING__COUNTED_SERVICES_DEFINITION,
    MOVING__SERVICES_DEFINITION,
    MovingServicesSchema,
} from "@root/server/routes/web/order_wizard/[order_edit_id]/moving/moving.shared.ts";
import type {InferInput} from "valibot";
import {ReactWrapperController} from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import {Context} from "@hotwired/stimulus";
import {RhfPillCheckboxGroup} from "@root/server/client/esweb/components/ui/rhf/RhfPillCheckboxGroup.tsx";
import {ContinueSubmitButton} from "@root/server/client/esweb/src/pages/web/shared/continue-submit-button.tsx";
import {FormServerErrors} from "@root/server/client/esweb/src/pages/web/shared/form-server-errors.tsx";
import {useFormSubmission} from "@root/server/client/esweb/src/lib/hooks/useFormSubmission.ts";
import {MovingHeavyItemsField} from "./components/moving-heavy-items-field.tsx";
import {Input} from "@root/server/client/esweb/components/ui/input.tsx";
import {Label} from "@root/server/client/esweb/components/ui/label.tsx";
import {Field, FieldError} from "@root/server/client/esweb/components/ui/field.tsx";
import {RhfObjectKeyCheckboxGroup} from "@root/server/client/esweb/components/ui/rhf/RhfObjectKeyCheckboxGroup.tsx";
import {useLens} from "@hookform/lenses";
import {RhfSpinInput} from "@root/server/client/esweb/components/ui/rhf/RhfSpinInput.tsx";

export const name = 'pwm--moving_services_form';

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <MovingServicesFormStimulus {...props}/>);
    }
}


export type MovingServicesFormValues = InferInput<typeof MovingServicesSchema>;

interface MovingServicesFormProps {
    initialData?: Partial<MovingServicesFormValues>;
    submitUrl: string;
}

const defaultCountedServices = Object.fromEntries(
    MOVING__COUNTED_SERVICES_DEFINITION.map((sd) => [sd.key, 0] as const),
) as Record<(typeof MOVING__COUNTED_SERVICES_DEFINITION)[number]["key"], number>;


export function MovingServicesFormStimulus({initialData, submitUrl}: MovingServicesFormProps) {


    const {control, handleSubmit, watch} = useForm<MovingServicesFormValues>({
        resolver: valibotResolver(MovingServicesSchema),
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            heavyItems: [],
            booleanServices: {},
            countedServices: defaultCountedServices,
            ...(initialData ?? {}),
        },
    });

    const lens = useLens({control});

    const {execute, isSubmitting, serverErrors} = useFormSubmission<MovingServicesFormValues>({submitUrl});

    return (
        <form onSubmit={handleSubmit(execute)} className="space-y-4">

            <section className="p-4 max-sm:-mx-4">
                <div className="space-y-4">

                    {MOVING__COUNTED_SERVICES_DEFINITION.map(({key, label, unit}) => (
                        <Controller
                            key={key}
                            control={control}
                            name={`countedServices.${key}` as const}
                            render={({field, fieldState}) => (
                                <Field data-invalid={fieldState.invalid}>
                                    <div  className="flex flex-wrap items-center gap-3">
                                        <Label htmlFor={`${key}-count`} className="text-sm font-medium w-full max-w-[220px]">
                                            {label}
                                        </Label>
                                        <Input
                                            id={`${key}-count`}
                                            {...field}
                                            type="number"
                                            min={0}
                                            className="w-24"
                                            onChange={(event) => field.onChange(Number.parseInt(event.target.value))}
                                        />
                                        <span className="text-sm text-muted-foreground">{unit}</span>
                                    </div>
                                    {fieldState.invalid && (
                                        <FieldError errors={[fieldState.error]} />
                                    )}
                                </Field>
                            )}
                        />
                    ))}

                    {/*{MOVING__COUNTED_SERVICES_DEFINITION.map(({key, label, unit}) => (*/}
                    {/*    <RhfSpinInput {...{min: 0, max: 1000, lens: lens.focus(`countedServices.${key}`)}}/>*/}
                    {/*))}*/}
                </div>
            </section>

            <section className="p-4 max-sm:-mx-4">
                <RhfObjectKeyCheckboxGroup
                    lens={lens.focus('booleanServices')}
                    label="Služby"
                    options={MOVING__SERVICES_DEFINITION}
                />
            </section>

            <section className="p-4 max-sm:-mx-4">
                <MovingHeavyItemsField control={control} name="heavyItems" />
            </section>

            <FormServerErrors errors={serverErrors} />

            <ContinueSubmitButton {...{isSubmitting}}/>

        </form>
    );
}
